import React from 'react';
import {
  ArticleBulletList,
  ArticleExample,
  ArticleParagraph,
  ArticleSection,
  ArticleSubsection,
} from './article-shared';

export const TherapyDocumentationGuideArticle: React.FC = () => (
  <>
    <ArticleSection title="Purpose of Documentation Evaluation">
      <ArticleParagraph>
        At BetterHelp, clinical documentation is a cornerstone of high-quality, ethical, and effective mental health care.
        To support providers in maintaining documentation that meets clinical, ethical, and regulatory standards, we
        conduct regular Chart Audits using a standardized set of criteria. This process is intended to be supportive as
        a quality assurance mechanism to promote best practices, ensure clinical appropriateness, and enhance client
        outcomes.
      </ArticleParagraph>
      <ArticleParagraph>
        This document outlines how each category of documentation is evaluated and how providers can ensure compliance
        with standards.{' '}
        <strong>
          Providers who consistently meet chart audit criteria demonstrate a strong commitment to excellence in client
          care and professional practice.
        </strong>
      </ArticleParagraph>
      <ArticleParagraph>
        <strong>Quick Tip:</strong> Think of your notes as a <em>clinical story</em> that shows the client’s journey:
        why they need care, what you’re doing, how they’re responding, and how safety is being managed.
      </ArticleParagraph>
    </ArticleSection>

    <ArticleSection title="I. Intake Assessment">
      <ArticleParagraph>
        The intake assessment forms the foundation of treatment. It is evaluated for completeness, clinical judgment,
        and timeliness.
      </ArticleParagraph>
      <ArticleSubsection title="Key Evaluation Points">
        <ArticleBulletList
          items={[
            'Obtain and document the client’s consent to receive treatment via telehealth during the intake session.',
            'Document confirmation of client’s identity and location (state) at the start of each session.',
            'Review any prefilled information with the client to confirm it is still accurate.',
            'Diagnostic Rationale & Medical Necessity: The summary must clearly justify the diagnosis and document why treatment is necessary.',
            'Functioning (ADLs): An assessment of the client’s ability to perform daily activities and how impairments relate to their mental health.',
            'Mental Status Exam (MSE): A comprehensive MSE should be included and summarized to inform treatment planning. The provider should review and update this section at each session.',
            'Suicide/Harm Risk: Documentation must address past and present ideation, intention, means and history of attempts associated with safety concerns.',
            'Safety Planning: If risk is identified, a safety plan and documentation of continued risk evaluation are expected.',
            'Scope of Practice: The therapist’s interventions must align with outpatient teletherapy standards and medical necessity.',
            'Timeliness: Intake documentation must be completed within 24-48 hours of the session.',
          ]}
        />
      </ArticleSubsection>
      <ArticleExample title="Example">
        Client provided informed consent to receive treatment via telehealth. Therapist confirmed the client’s identity
        and location (state) at the start of the session. Client is a 22 year old divorced Asian male referred to BetterHelp
        by his PCP to address symptoms of depression and anxiety. Client indicates that his current symptoms are: xyz.
        Client has a family history positive for xyz and further reports a history of abuse xyz. Client struggles with
        the following stressors, including xyz and has a medical history of xyz. Client presents as oriented x3 with
        depressed mood and congruent affect. He denies any active suicidal ideation and denies any history of suicidal
        attempt. He does endorse a history of self-harming during the years of 2010-2012 but presently denies any
        ideation. Client meets the clinical criteria for xyz diagnoses and it is recommended that client attend
        individual therapy 1x/week with therapist for a minimum of x months.
      </ArticleExample>
    </ArticleSection>

    <ArticleSection title="II. Treatment Plan">
      <ArticleParagraph>
        The treatment plan is evaluated for relevance, structure, and alignment with the intake assessment.
      </ArticleParagraph>
      <ArticleSubsection title="Key Evaluation Points">
        <ArticleBulletList
          items={[
            'Diagnosis Support: Diagnoses should align with symptom presentation and justify ongoing treatment.',
            'SMART Goals: Goals and objectives must be Specific, Measurable, Achievable, Relevant, and Time-bound, and correspond to client symptoms.',
            'Timeliness: Treatment Plans should be documented by session 2.',
          ]}
        />
      </ArticleSubsection>
      <ArticleExample title="Example">
        <strong>Problem Description:</strong> Client indicates that he is experiencing symptoms consistent with
        depression for the past four (4) months including: loss of interest, insomnia, poor concentration, poor
        appetite, sadness/depressed mood, and lethargy.
        <br /><br />
        <strong>Treatment Goal:</strong> Client will decrease symptoms of depression through active use of coping
        strategies and regular individual therapy.
        <br /><br />
        <strong>Treatment Objective:</strong> Client will implement three (3) coping skills per week to assist with
        decreasing the severity of his depression symptoms, implement two (2) sleep hygiene strategies to assist with
        his sleep cycle and make one (1) change to his diet per week to assist with symptom management.
        <br /><br />
        <strong>Treatment Intervention:</strong> Client will attend individual therapy weekly. Client will implement
        sleep hygiene strategies, CBT strategies (behavior activation, cognitive restructuring) and solution-focused
        interventions to support symptom management.
      </ArticleExample>
    </ArticleSection>

    <ArticleSection title="III. Progress Notes">
      <ArticleParagraph>
        Progress notes are evaluated to ensure clinical relevance, appropriate interventions, documentation quality,
        medical necessity, and evidence of patient progress over time.
      </ArticleParagraph>
      <ArticleSubsection title="Key Evaluation Points">
        <ArticleBulletList
          items={[
            'Document confirmation of client’s identity and location (state) at the start of each session.',
            'Symptom Tracking: Clear documentation of current symptoms and functional impairments.',
            'Evidence-Based Interventions: At least one therapeutic method or technique should be noted per session.',
            'Progress Toward Goals: Narrative should reflect improvements, stagnation, or regression with rationale.',
            'Adjustments to Treatment: Modifications should be noted when needed due to limited progress or worsening symptoms.',
            'Mental Status: Include MSE observations that reflect the client’s current functioning.',
            'Risk Management: If risk is present, document assessment and any safety planning conducted.',
            'Session Details: Ensure the session duration and CPT code match.',
            'Engagement Level: The client’s participation should be noted to help inform care planning.',
            'Planning: Each note should include a documented plan for the next session.',
            'Scope of Practice: Providers must demonstrate clinical judgment aligned with outpatient therapy.',
            'Timeliness: Documentation should be completed within 24-48 hours of the session.',
          ]}
        />
      </ArticleSubsection>
      <ArticleExample title="Example — Clinical Summary (SOAP)">
        <strong>S (Subjective):</strong> Client reported a slight improvement in mood over the past week, describing
        herself as “less numb and more hopeful.” They attributed part of this improvement to engaging in a difficult but
        successful interpersonal task—initiating a conversation with a coworker, which they had previously avoided due
        to anxiety. Despite this, client expressed ongoing worry regarding performance at work and continues to
        experience anticipatory anxiety. They described persistent sleep disruption and occasional fatigue, but denied
        any suicidal ideation, intent, or plan at this time.
        <br /><br />
        <strong>O (Objective):</strong> The therapist confirmed the client’s identity and location (state) at the start
        of the session. Client presented on time, appropriately groomed, and oriented to person, place, time, and
        situation. Affect was congruent and ranged from neutral to mildly hopeful. Client completed CBT thought record
        homework as assigned. No overt signs of distress or crisis were noted during the session.
        <br /><br />
        <strong>A (Assessment):</strong> Client continues to present with clinically significant symptoms of depression
        and generalized anxiety, but is beginning to show early improvement in mood and functioning. Treatment remains
        medically necessary due to continued impairment in occupational and social functioning.
        <br /><br />
        <strong>P (Plan):</strong> Treatment will continue with weekly individual CBT sessions focused on behavioral
        activation and cognitive restructuring. Client was assigned a new thought record targeting performance-related
        anxiety. Therapist introduced initial sleep hygiene strategies. Next appointment is scheduled.
      </ArticleExample>
    </ArticleSection>

    <ArticleSection title="IV. Discharge Summary">
      <ArticleParagraph>
        The discharge summary is evaluated for completeness and appropriate closure of services.
      </ArticleParagraph>
      <ArticleSubsection title="Key Evaluation Points">
        <ArticleBulletList
          items={[
            'Timeliness: Must be completed within 24-48 hours of the discharge session or termination of services.',
            'Reason for Discharge',
            'Discharge Diagnosis',
            'Prognosis',
            'Continuing Care Recommendations',
            'Discharge Summary',
          ]}
        />
      </ArticleSubsection>
      <ArticleExample title="Example Discharge Summary">
        Client no longer meets full criteria for Major Depressive Disorder and demonstrates significantly reduced
        symptoms of Generalized Anxiety Disorder. Residual symptoms are mild and manageable using learned coping
        strategies. Client presents as stable and capable of maintaining functioning without regular therapy at this
        time.
      </ArticleExample>
    </ArticleSection>

    <ArticleSection title="Documentation Quick Reference Guide">
      <ArticleSubsection title="Do’s">
        <ArticleBulletList
          items={[
            'Obtain and document client’s consent for telehealth at intake; document confirmation of client’s identity and location (state) at the start of each session.',
            'Complete intake documentation within 24-48 hours of the first session (include presenting concerns, diagnostic rationale, functioning, MSE, and risk assessment).',
            'Document medical necessity: clearly justify diagnosis and why ongoing treatment is clinically appropriate.',
            'Assess and document risk (suicidal/homicidal ideation, self-harm, safety planning if indicated) at intake and ongoing.',
            'Develop SMART treatment goals that align with client’s symptoms and needs.',
            'Create a treatment plan within 2 sessions and revise as clinically indicated.',
            'Use progress notes to show continuity: track symptoms, interventions, client engagement, and progress toward goals.',
            'Include at least one evidence-based intervention per session (e.g., CBT, DBT, solution-focused strategies).',
            'Document session details accurately (duration, CPT code, plan for next session).',
            'Complete all notes within 24-48 hours of session; complete discharge summary promptly (within 24–48 hours).',
            'Keep notes professional, objective, and clinically relevant (avoid excessive personal detail).',
          ]}
        />
      </ArticleSubsection>
      <ArticleSubsection title="Don’ts">
        <ArticleBulletList
          items={[
            'Don’t skip documenting consent or location checks (location must be confirmed each session).',
            'Don’t leave risk assessments vague (always specify ideation, intent, means, plan, history).',
            'Don’t use overly general goals (“feel better,” “reduce anxiety”) — use SMART goals.',
            'Don’t copy-paste the same intervention or progress note language repeatedly; show client-specific changes over time.',
            'Don’t delay documentation beyond required timelines.',
            'Don’t include interventions outside your scope of practice (e.g., medical management if you’re not licensed for it).',
            'Don’t discharge a client without a summary of progress, current status, and referrals.',
            'Don’t forget HIPAA: avoid unnecessary identifying details not relevant to treatment.',
          ]}
        />
      </ArticleSubsection>
    </ArticleSection>
  </>
);
